<template>
    <div>
        <div @click.stop="generateReport" title="Export Excel"
            class="flex-shrink-0 cursor-pointer p-2 rounded-md bg-primary-blue-bg text-primary-blue-text hover:bg-primary-blue-bg-dark hover:text-primary-blue-text-dark transition-colors duration-200 shadow-sm">
            <font-awesome-icon :icon="['fas', 'file-excel']" class="w-5 h-5" />
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";

const GET_ALL_EMPLOYED_STUDENTS = gql`
    query ExportEmployedStudents(
        $page: Int
        $limit: Int
        $filters: StudentFilterInput!
    ) {
        exportEmployedStudents(
        page: $page
        limit: $limit
        filters: $filters
    ) {
        error
        error_code
        message
        data {
            student_id
            eid
            name_en
            entity
            campus_code
            gender
            program_name
            academic_year
            grad_term
            local_or_non_local
            eligible_for_employment
            is_employed
            current_status
            employer
            is_job_rel_major
            grad_date
            student_employment_profile {
                first_joining_date
            }
            got_job_within
            employmentSource {
                label
            }
            is_licensed
            student_license {
                eid
                license_number
                license_status
            }
            employmentInformations {
                salary_range
            }
        }
    }
}`;

function runDetachedExport(ctx) {
    var store = ctx.store;
    var apolloClient = ctx.apolloClient;
    var jobId = ctx.jobId;
    var fileName = ctx.fileName;
    var filters = ctx.filters;
    var columns = ctx.columns;
    var mapRow = ctx.mapRow;

    var allRows = [];
    var page = 1;
    var limit = 200;

    function fetchNext() {
        apolloClient
            .query({
                query: GET_ALL_EMPLOYED_STUDENTS,
                fetchPolicy: "network-only",
                variables: { page: page, limit: limit, filters: filters },
            })
            .then(function (result) {
                var responseData = result && result.data
                    ? result.data.exportEmployedStudents
                    : null;

                if (!responseData || responseData.error) {
                    throw new Error(
                        (responseData && responseData.message) || "Server error"
                    );
                }

                if (responseData.data && responseData.data.length > 0) {
                    for (var i = 0; i < responseData.data.length; i++) {
                        allRows.push(mapRow(responseData.data[i]));
                    }

                    var percent = Math.min(95, Math.round(
                        (allRows.length / Math.max(allRows.length + limit, 1)) * 100
                    ));

                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: percent,
                        currentSheet: "Employed Students (" + allRows.length + " rows)",
                    });

                    page++;
                    fetchNext();
                } else {
                    finishExport();
                }
            })
            .catch(function (error) {
                if (allRows.length > 0) {
                    finishExport();
                } else {
                    store.dispatch("exportManager/failJob", {
                        id: jobId,
                        error: (error && error.message) || "Export failed",
                    });
                }
            });
    }

    function finishExport() {
        try {
            if (allRows.length === 0) {
                store.dispatch("exportManager/failJob", {
                    id: jobId,
                    error: "No data available for export",
                });
                return;
            }

            var keys = columns.map(function (col) { return col.key; });

            var rows = allRows.map(function (row) {
                var out = {};
                for (var i = 0; i < columns.length; i++) {
                    var key = columns[i].key;
                    out[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
                }
                return out;
            });

            var worksheet = xlsx.utils.json_to_sheet(rows, { header: keys });

            var columnWidths = [];
            for (var i = 0; i < columns.length; i++) {
                var column = String.fromCharCode(65 + (i % 26));
                var r = Math.floor(i / 26);
                var columnLetter = r > 0 ? String.fromCharCode(64 + r) + column : column;
                worksheet[columnLetter + "1"] = { v: columns[i].header, t: "s" };
                columnWidths.push({ width: Math.max(40, columns[i].header.length + 5) });
            }

            worksheet["!cols"] = columnWidths;

            var workbook = xlsx.utils.book_new();
            xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
            xlsx.writeFile(workbook, fileName);

            store.dispatch("exportManager/completeJob", {
                id: jobId,
                rowCount: allRows.length,
            });
        } catch (error) {
            store.dispatch("exportManager/failJob", {
                id: jobId,
                error: (error && error.message) || "Export failed",
            });
        }
    }

    fetchNext();
}

export default {
    name: "export-all-excel-employment-dashboard",
    props: ["filters"],
    computed: {
        role() {
            return this.$store.getters.role;
        },
        shouldExportSalaryRange() {
            var entity = this.filters && this.filters.entity ? this.filters.entity : "";
            return (
                this.role == "superadmin" &&
                (entity == "FCHS" || entity == "ADPOLY")
            );
        },
    },
    methods: {
        generateReport() {
            var store = this.$store;
            var apolloClient = this.$apollo && this.$apollo.provider
                ? this.$apollo.provider.defaultClient
                : null;
            if (!apolloClient) return;

            var fileName = "EMPLOYMENT_DASHBOARD_REPORT.xlsx";

            // Snapshot filters and columns before component might be destroyed
            var filtersCopy = JSON.parse(JSON.stringify(this.filters || {}));
            var columns = this.getEmploymentExportColumns();
            var snapshotSalaryRange = this.shouldExportSalaryRange;
            var self = this;

            var mapRow = function (obj) {
                var row = {
                    student_id: obj.student_id,
                    eid: obj.eid,
                    name_en: obj.name_en,
                    entity: self.getEntityName(obj.entity),
                    campus_code: obj.campus_code,
                    gender: self.getGender(obj.gender),
                    program_name: obj.program_name,
                    academic_year: obj.academic_year,
                    semester: obj.grad_term,
                    local_or_non_local: obj.local_or_non_local,
                    eligible_for_employment: self.isEligibleForEmployment(obj.eligible_for_employment),
                    is_employed: self.isEmployed(obj.is_employed),
                    current_status: obj.current_status,
                    employer: self.getEmployer(obj.is_employed, obj.employer),
                };
                if (snapshotSalaryRange) {
                    row.salary_range = self.getSalaryRange(obj.employmentInformations);
                }
                row.is_job_rel_major = self.isJobRelatedToMajor(obj.is_employed, obj.is_job_rel_major);
                row.graduation_date = self.formatDateForExport(obj.grad_date);
                row.employment_joining_date = self.formatDateForExport(
                    obj.student_employment_profile && obj.student_employment_profile.first_joining_date
                );
                row.got_job_within = obj.got_job_within;
                row.employment_source = self.getEmploymentSource(obj.is_employed, obj.employmentSource);
                row.licensed = self.formatLicensedForTable(obj);
                row.license_number = self.licenseNumberForTable(obj);
                row.license_status = self.licenseStatusForTable(obj);
                return row;
            };

            store
                .dispatch("exportManager/addJob", {
                    fileName: fileName,
                    totalSheets: 1,
                })
                .then(function (jobId) {
                    store.dispatch("exportManager/setMinimized", false);
                    store.dispatch("exportManager/updateJob", {
                        id: jobId,
                        progress: 5,
                        currentSheet: "Employed Students",
                    });

                    runDetachedExport({
                        store: store,
                        apolloClient: apolloClient,
                        jobId: jobId,
                        fileName: fileName,
                        filters: filtersCopy,
                        columns: columns,
                        mapRow: mapRow,
                    });
                });
        },
        getEmploymentExportColumns() {
            var columns = [
                { key: "student_id", header: "STUDENT ID" },
                { key: "eid", header: "EMIRATES ID" },
                { key: "name_en", header: "NAME" },
                { key: "entity", header: "ENTITY" },
                { key: "campus_code", header: "CAMPUS" },
                { key: "gender", header: "GENDER" },
                { key: "program_name", header: "PROGRAM NAME" },
                { key: "academic_year", header: "ACADEMIC YEAR" },
                { key: "semester", header: "SEMESTER" },
                { key: "local_or_non_local", header: "LOCAL OR NON LOCAL" },
                { key: "eligible_for_employment", header: "IS ELIGIBLE FOR EMPLOYMENT" },
                { key: "is_employed", header: "IS EMPLOYED" },
                { key: "current_status", header: "CURRENT STATUS" },
                { key: "employer", header: "EMPLOYER" },
            ];

            if (this.shouldExportSalaryRange) {
                columns.push({ key: "salary_range", header: "SALARY RANGE" });
            }

            columns.push(
                { key: "is_job_rel_major", header: "IS JOB RELATED TO MAJOR" },
                { key: "graduation_date", header: "GRADUATION DATE" },
                { key: "employment_joining_date", header: "EMPLOYMENT JOINING DATE" },
                { key: "got_job_within", header: "GOT JOB WITHIN" },
                { key: "employment_source", header: "EMPLOYMENT SOURCE" },
                { key: "licensed", header: "LICENSED" },
                { key: "license_number", header: "LICENSE NUMBER" },
                { key: "license_status", header: "LICENSE STATUS" }
            );

            return columns;
        },
        getSalaryRange(employmentInformations) {
            if (employmentInformations && employmentInformations.salary_range) {
                return employmentInformations.salary_range;
            }
            return "";
        },
        formatDateForExport(value) {
            if (value === null || value === undefined || value === "") {
                return "";
            }
            var s = String(value);
            if (s.indexOf("T") !== -1) {
                s = s.split("T")[0];
            }
            return s;
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        getGender(value) {
            if (value) {
                if (value == "M") {
                    return "Male";
                } else {
                    return "Female";
                }
            } else {
                return "";
            }
        },
        isJobRelatedToMajor(isEmployed, value) {
            if (isEmployed === null) {
                return "";
            } else {
                if (isEmployed) {
                    if (value === null) {
                        return "Not Updated";
                    } else {
                        if (value) {
                            return "Yes";
                        } else {
                            return "No";
                        }
                    }
                } else {
                    return "";
                }
            }
        },
        isEligibleForEmployment(eligibleForEmployment) {
            if (eligibleForEmployment === null) {
                return "";
            } else {
                if (eligibleForEmployment) {
                    return "Yes";
                } else {
                    return "No";
                }
            }
        },
        isEmployed(value) {
            if (value === null) {
                return "Not Contacted";
            } else {
                if (value) {
                    return "Employed";
                } else {
                    return "Unemployed";
                }
            }
        },
        getEmployer(isEmployed, employerName) {
            if (isEmployed === null) {
                return "";
            } else {
                if (isEmployed) {
                    if (employerName) {
                        return employerName;
                    } else {
                        return "Employed";
                    }
                } else {
                    return "Unemployed";
                }
            }
        },
        getEmploymentSource(isEmployed, employmentSource) {
            if (isEmployed === null || !isEmployed) {
                return "";
            }
            if (employmentSource && employmentSource.label) {
                return employmentSource.label;
            }
            return "";
        },
        formatLicensedForTable(student) {
            if (!student) {
                return "";
            }
            if (student.is_licensed === true || student.is_licensed === 1) {
                return "Yes";
            }
            if (student.is_licensed === false || student.is_licensed === 0) {
                return "No";
            }
            return "";
        },
        licenseNumberForTable(student) {
            if (!student || !student.student_license) {
                return "";
            }
            var n = student.student_license.license_number;
            if (n === null || n === undefined || String(n).trim() === "") {
                return "";
            }
            return n;
        },
        licenseStatusForTable(student) {
            if (!student || !student.student_license) {
                return "";
            }
            var s = student.student_license.license_status;
            if (s === null || s === undefined || String(s).trim() === "") {
                return "";
            }
            return s;
        },
    },
}
</script>
